if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        const pent3dThumbnail = this.querySelector('.pent-3d-thumbnail');
        pent3dThumbnail?.addEventListener('click', () => {
          const viewer = this.querySelector('pent-3d-viewer');
          if (!viewer) return;
          window.sportovaBeginPent3dLoading?.(viewer);
          this.setActiveMedia(viewer.dataset.galleryId, false);
          if (!document.querySelector('script[data-pent3d-runtime], script[data-pent-3d-runtime]')) {
            const runtime = document.createElement('script');
            runtime.src = viewer.dataset.runtime;
            runtime.dataset.pent3dRuntime = 'true';
            document.head.appendChild(runtime);
          } else {
            viewer.activate?.();
          }
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const mediaItems = this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]');
        const activeMedia = Array.from(mediaItems).find((element) => element.dataset.mediaId === mediaId);
        if (!activeMedia) return;
        const previousMedia = Array.from(mediaItems).find((element) => element.classList.contains('is-active'));
        const activeImage = activeMedia.querySelector('.product__media img');
        const thumbnailImage = this.elements.thumbnails?.querySelector(`[data-target="${mediaId}"] img`);
        const previousImage = previousMedia?.querySelector('.product__media img');
        const previewSource = thumbnailImage?.currentSrc || thumbnailImage?.src || previousImage?.currentSrc || previousImage?.src;
        let transitionPreview = this.elements.viewer.querySelector('.sportova-progressive-image');

        if (activeImage && previewSource && previousMedia !== activeMedia && activeImage.dataset.sportovaReadyForSwitch !== 'true') {
          this.transitionMediaId = mediaId;
          transitionPreview?.remove();
          transitionPreview = document.createElement('div');
          transitionPreview.className = 'sportova-progressive-image';
          transitionPreview.style.backgroundImage = `url("${previewSource.replace(/"/g, '%22')}")`;
          transitionPreview.setAttribute('aria-hidden', 'true');
          this.elements.viewer.appendChild(transitionPreview);
          activeImage.classList.add('sportova-progressive-image--loading');
          activeImage.loading = 'eager';
          activeImage.fetchPriority = 'high';

          let switchRequested = false;
          const switchWhenReady = () => {
            if (switchRequested) return;
            switchRequested = true;
            const decoded = activeImage.decode ? activeImage.decode().catch(() => {}) : Promise.resolve();
            const minimumPreview = new Promise((resolve) => window.setTimeout(resolve, 180));
            Promise.all([decoded, minimumPreview]).finally(() => {
              if (this.transitionMediaId !== mediaId) return;
              activeImage.dataset.sportovaReadyForSwitch = 'true';
              this.setActiveMedia(mediaId, prepend);
            });
          };

          if (activeImage.complete && activeImage.naturalWidth) switchWhenReady();
          else {
            activeImage.addEventListener('load', switchWhenReady, { once: true });
            activeImage.addEventListener('error', switchWhenReady, { once: true });
            activeImage.src = activeImage.currentSrc || activeImage.src;
          }
          window.setTimeout(switchWhenReady, 6000);
          return;
        }

        mediaItems.forEach((element) => {
          element.classList.remove('is-active');
          element.setAttribute('aria-hidden', 'true');
          element.querySelector('pent-3d-viewer, pent-mimeeq-viewer')?.setActive?.(false);
        });
        activeMedia.classList.add('is-active');
        activeMedia.removeAttribute('aria-hidden');
        activeMedia.querySelector('pent-3d-viewer, pent-mimeeq-viewer')?.setActive?.(true);

        if (activeImage) {
          activeImage.loading = 'eager';
          activeImage.fetchPriority = 'high';
          delete activeImage.dataset.sportovaReadyForSwitch;
          this.transitionMediaId = null;
          window.requestAnimationFrame(() => window.requestAnimationFrame(() => {
            this.elements.viewer.querySelectorAll('.sportova-variant-image--pending').forEach((image) => image.classList.remove('sportova-variant-image--pending'));
            activeImage.classList.remove('sportova-progressive-image--loading');
            transitionPreview?.classList.add('sportova-progressive-image--revealed');
            window.setTimeout(() => transitionPreview?.remove(), 620);
          }));
        }

        const pentViewer = activeMedia.querySelector('pent-3d-viewer');
        if (pentViewer) {
          window.sportovaBeginPent3dLoading?.(pentViewer);
          if (!document.querySelector('script[data-pent3d-runtime], script[data-pent-3d-runtime]')) {
            const runtime = document.createElement('script');
            runtime.src = pentViewer.dataset.runtime;
            runtime.dataset.pent3dRuntime = 'true';
            document.head.appendChild(runtime);
          } else {
            pentViewer.activate?.();
          }
        }

        if (prepend) {
          activeMedia.parentElement.prepend(activeMedia);
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.prepend(activeThumbnail);
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          if (!prepend && (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked')) {
            activeMedia.scrollIntoView({ behavior: 'smooth' });
          }
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
